<template>
  <v-dialog
    v-model="dialog"
    fullscreen
    hide-overlay
    transition="dialog-bottom-transition"
    persistent
  >
    <v-card class="ma-0 pa-0 d-flex flex-column merge-card">
      <v-card-actions class="ma-0 px-2 py-0">
        <v-card-title class="deep-orange--text">
          {{ hasSource ? sourceConfig.group : '-' }}
        </v-card-title>
        <template v-if="targetConfig">
          <span class="grey--text text--lighten-1 mx-3">|</span>
          <span class="caption grey--text">{{ targetOwner }}</span>
          <span class="grey--text text--lighten-1 mx-3">|</span>
          <span class="caption grey--text">{{ targetUpdated | formatDate }}</span>
          <span class="grey--text text--lighten-1 mx-3">|</span>
          <v-chip
            small
            label
            :color="isTargetDeployed ? 'green' : 'red'"
            text-color="white"
          >
            {{ isTargetDeployed ? 'Deployed' : 'Not Deployed' }}
          </v-chip>
        </template>
        <v-spacer></v-spacer>
        <v-btn icon class="ma-2" @click="onClose">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-actions>

      <v-card-actions class="ma-0 pa-0 mx-4 mb-3">
        <v-card elevation="0" class="white--text px-0" outlined width="100%">
          <v-card-actions class="ma-0">
            <v-btn
              :color="getActiveColor(1)"
              outlined
              elevation="0"
              @click="onPressTab(1)"
            >
              My Configs
            </v-btn>

            <v-btn
              :color="getActiveColor(2)"
              outlined
              elevation="0"
              @click="onPressTab(2)"
            >
              Pre Release Configs
            </v-btn>
            <v-card elevation="0" class="white--text px-0 ml-2" width="100%">
              <v-autocomplete
                class="ma-0 pa-0"
                v-model="targetConfig"
                dense
                height="30px"
                :items="getEndConfigs()"
                outlined
                item-text="group"
                item-value="uid"
                return-object
                small-chips
                deletable-chips
                hide-details
                @change="updateInput()"
              />
            </v-card>
          </v-card-actions>
        </v-card>
      </v-card-actions>

      <v-card-actions class="ma-0 pa-0 mx-0 mt-n2 flex-grow-1 min-h-0">
        <v-card outlined width="100%" class="mx-4 d-flex flex-column fill-height min-h-0">
          <template v-if="downloadData">
            <div class="flex-grow-1 d-flex align-center justify-center">
              <v-progress-circular indeterminate color="primary"></v-progress-circular>
            </div>
          </template>
          <template v-else-if="sourceConfig == null || targetConfig == null">
            <div class="flex-grow-1 d-flex align-center justify-center">
              <v-card-title class="grey--text">Select a config to compare with</v-card-title>
            </div>
          </template>

          <div class="flex-grow-1 min-h-0 overflow-y-auto">
            <v-card-actions class="ma-0 pa-0">
              <v-list width="100%" dense>
                <v-list-group
                  v-for="group in groups"
                  :key="group.name"
                  :append-icon="''"
                >
                  <v-card height="40px" class="ml-4 mr-4" elevation="0">
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn small icon @click="onSelectaAll(group, 'a')" color="orange"><v-icon>mdi-checkbox-multiple-outline</v-icon></v-btn>
                      <v-btn small icon @click="onSelectaAll(group, 'b')" color="blue"><v-icon>mdi-checkbox-multiple-outline</v-icon></v-btn>
                      <v-btn small icon @click="onDeselectAll(group)" color="grey"><v-icon>mdi-eraser</v-icon></v-btn>
                    </v-card-actions>
                  </v-card>

                  <template v-slot:activator>
                    <v-list-item-content class="pa-0 mx-0">
                      <v-btn v-if="group.code === 0" style="width: 100%;" color="green" class="white--text" outlined>
                        <v-icon>mdi-check-circle</v-icon>
                        <h5 style="margin-left: 10px">{{group.name}}</h5>
                        <v-spacer></v-spacer>
                      </v-btn>
                      <v-btn v-else style="width: 100%;" :color="getGroupColor(group)" class="white--text" elevation="0">
                        <v-icon style="margin-right: 10px">mdi-alert-circle-outline</v-icon>
                        <h5 class="d-flex justify-space-between" style="width: 100%">
                          {{ group.name }}

                          <span v-if="groupHasSelection(group)">
                            [A: {{ getGroupSelectionCounts(group).a }}]
                            [B: {{ getGroupSelectionCounts(group).b }}]
                            [C: {{ getGroupSelectionCounts(group).c }}]
                          </span>
                        </h5>
                        <v-spacer></v-spacer>
                      </v-btn>
                    </v-list-item-content>
                  </template>

                  <v-list-item v-for="item in group.items" :key="item.uid">
                    <v-list-item-content class="pa-0">
                      <v-btn :color="getItemColor(item)" class="white--text ma-0 pa-0" elevation="0">
                        <v-icon size="20" class="ml-5">mdi-alert-circle</v-icon>

                        <h5 class="ml-3">
                          {{ item.name }}
                        </h5>

                        <v-spacer></v-spacer>

                        <v-checkbox
                          @change="checkboxUpdated(item.group + '/' + item.uid, 'a', $event)"
                          :value="checkedA(item)"
                          color="white"
                          light
                        >
                          <span slot="label" class="white--text">{{ labelA }}</span>
                        </v-checkbox>

                        <v-checkbox
                          @change="checkboxUpdated(item.group + '/' + item.uid, 'b', $event)"
                          :value="checkedB(item)"
                          color="white"
                          light
                          class="ml-7"
                        >
                          <span slot="label" class="white--text">{{ labelB }}</span>
                        </v-checkbox>

                        <v-checkbox
                          v-if="resolved[item.group + '/' + item.uid]?.c"
                          :input-value="isCustomChecked(item)"
                          color="white"
                          light
                          disabled
                          class="ml-7"
                        >
                          <span slot="label" class="white--text">CUSTOM</span>
                        </v-checkbox>

                        <v-divider vertical class="ml-5" style="background-color: white"></v-divider>

                        <v-btn icon color="white" class="ml-2 mr-2" @click="showItemDetails(item)">
                          <v-icon size="22">mdi-merge</v-icon>
                        </v-btn>
                      </v-btn>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-group>
              </v-list>
            </v-card-actions>
          </div>
        </v-card>
      </v-card-actions>

      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          :disabled="isNotResolved() || sourceConfig == null || targetConfig == null || downloadData"
          class="ma-2 white--text px-3"
          color="blue"
          elevation="0"
          @click="onPressSaveToMyConfigs"
        >
          Merge and save to My Configs
          <v-icon class="ml-3">mdi-content-save</v-icon>
        </v-btn>
        <v-btn
          :disabled="true || !ifCanDeploy()"
          class="ma-2 white--text px-3"
          color="green"
          elevation="0"
          @click="onPressDeployToRelease"
        >
          Deploy to Release
          <v-icon class="ml-3">mdi-content-save-move</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {ConfigStoreUtils} from "@/modules/mconfigs/database/ConfigStoreUtils";
import {diff} from "deep-diff";
import {ConfigModel} from "@/modules/mconfigs/models/ConfigModel";
import {SlackLogManager} from "@/common/SlackLogManager";
import {PermissionsStoreUtils} from "@/modules/mpermissions/database/PermissionsStoreUtils";
import firebase from "firebase";
import {MergeHistoryModel} from "@/modules/mconfigs/models/MergeHistoryModel";
import {TimeUtils} from "@/common/TimeUtils";
import {uuid} from "vue-uuid";

export default {
  name: "config-merge-popup",
  props: {},
  components: {},
  data: () => ({
    dialog: false,
    sourceConfig:null,
    targetConfig:null,
    sourceData:null,
    targetData:null,
    stringC: {},
    groups:[],
    resolved:[],
    customValues: {},
    downloadData:false,
    selectedTab:1,
  }),
  computed: {
    myConfigs() {
      return ConfigStoreUtils.getConfigs();
    },
    releaseConfigs() {
      return ConfigStoreUtils.getReleaseConfigs();
    },
    masterConfigs() {
      return ConfigStoreUtils.getMasterConfigs();
    },
    hasSource() { return !!this.sourceConfig; },
    hasB() { return !!this.targetConfig; },
    labelA() { return (this.sourceConfig?.group || 'A').toLowerCase(); },
    labelB() { return this.targetConfig?.group || 'B'; },
    targetOwner() {
      return this.targetConfig?.meta?.uid
        ? PermissionsStoreUtils.getUserName(this.targetConfig.meta.uid)
        : '';
    },
    targetUpdated() {
      return this.targetConfig?.meta?.updated || '';
    },
    isTargetDeployed() {
      return !!this.targetConfig?.deployed;
    },
  },
  watch: {
    selectedTab(newVal) {
      if (newVal === 2) {
        this.reloadReleaseConfigs();
      }
    },
  },
  methods: {
    onDeselectAll(group){
      for (let i in group.items){
        const key = group.name + "/" + group.items[i].uid;
        this.resolved[key] = {a:false, b:false, c:false};
        delete this.customValues[key];
      }
      this.$forceUpdate();
    },
    onSelectaAll(group, tag){
      for (let i in group.items){
        let key = group.name + "/" + group.items[i].uid;
        this.resolved[key] = {a:false, b:false};
        if (tag === 'a'){
          this.resolved[key].a = true;
        } else {
          this.resolved[key].b = true;
        }
      }
      this.$forceUpdate();
    },
    ifCanDeploy(){
      if (this.selectedTab === 1 || this.selectedTab === 3){return false}
      if (this.targetConfig === null) {return false}
      if (this.downloadData) {return false}
      for (let i in this.resolved) {
        if (this.resolved[i].a === true || this.resolved[i].b === true || this.resolved[i].c === true){
          return false
        }
      }
      return true
    },
    onPressTab(tab){
      this.targetConfig = null
      this.selectedTab = tab
      this.clear();

      if (tab === 2){
        this.reloadReleaseConfigs();
      }
    },
    getActiveColor(tab){
      if (this.selectedTab === tab){
        return "blue"
      }
      return "grey"
    },
    getEndConfigs(){
      if (this.selectedTab === 1){
        return this.myConfigs
      }else if (this.selectedTab === 2){
        return this.releaseConfigs
      }else{
        return this.masterConfigs
      }
    },
    async onPressSaveToMyConfigs(){
      const isThereNewerVersion = await this.validateNewerVersion();

      if (isThereNewerVersion) {
        this.$alert("There is a new version of the target config, fix the new conflict to continue.");
        return;
      }

      if (!this.hasAnyDiffs(this.groups)) {
        this.save()
        return;
      }

      this.$confirm("Are you sure?").then(this.save).catch(() => {
        this.buttonLoading = null;
      })
    },
    save() {
      this.buttonLoading = true;
      let data = this.getFinalConfig();

      ConfigStoreUtils.pushConfig(data.config, data.content).then(() => {
        this.$notify({title: 'Config successfully merged!', type: "success"});
        this.dialog = false;
        this.buttonLoading = false;

        let historyModel = new MergeHistoryModel();
        historyModel.date = TimeUtils.now();
        historyModel.data.push({uid: this.sourceConfig.uid, owner:this.sourceConfig.meta.uid, data_path:this.getUUID(), "name":"a"});
        historyModel.data.push({uid: this.targetConfig.uid, owner:this.targetConfig.meta.uid, data_path:this.getUUID(), "name":"b"});
        historyModel.data.push({uid: data.config.uid, owner:firebase.auth().currentUser.uid, data_path:this.getUUID(), "name":"c"});

        let datas = [
          {"data_path": historyModel.data[0].data_path, "data_content":data.a},
          {"data_path": historyModel.data[1].data_path, "data_content":data.b},
          {"data_path": historyModel.data[2].data_path, "data_content":data.content}
        ]

        ConfigStoreUtils.pushMergeHistory(historyModel, datas);

      }).catch((error) => {
        console.log(error)
        this.$notify({title: 'Config merge failed!', text: error, type: "error"});
        this.dialog = false;
        this.buttonLoading = false;
      })
    },
    async onPressDeployToRelease(){
      const isThereNewerVersion = await this.validateNewerVersion();

      const confirmMessage = isThereNewerVersion ?
        "There is a new version of the target config. Deploying will overwrite the target config. Are you sure?" :
        "Are you sure?";

      if (!this.hasAnyDiffs(this.groups)) {
        this.deploy()
        return;
      }

      this.$confirm(confirmMessage)
          .then(this.deploy)
          .catch(() => {
        this.buttonLoading = null;
      })
    },
    deploy() {
      this.buttonLoading = true;

      let releaseConfig = this.targetConfig.clone()
      releaseConfig.deployed = false;
      releaseConfig.tag = this.sourceConfig.group;
      releaseConfig.info = this.sourceConfig.info;
      releaseConfig.sharedConfig = this.sourceConfig.sharedConfig;

      for (let item in this.sourceData){
        if (this.sourceData[item].length === 1 && Object.hasOwn(this.sourceData[item][0], 'one_value_property')){
          this.sourceData[item] = this.sourceData[item][0]["one_value_property"];
        }
      }

      ConfigStoreUtils.updateReleaseConfig(releaseConfig, this.sourceData).then(() => {
        this.$notify({title: 'Config successfully deployed to: ' + this.targetConfig.group, type: "success"});
        this.dialog = false;
        this.buttonLoading = false;
        this.sendToSlack();
      }).catch((error) => {
        console.log(error);
        this.$notify({title: 'Config deploy failed!', text: error, type: "error"});
        this.dialog = false;
        this.buttonLoading = false;
      })
    },
    sendToSlack(){
      let deployData = {
        'icon_emoji': ':hammer:',
        'text': "<@"+PermissionsStoreUtils.getMyName()+"> deploy config to `PRE RELEASE`",
        'attachments': [{
          'color': '#eed140',
          'fields': [
            {"title": "Environment", 'value': SlackLogManager.getEnv(), 'short': true},
            {"title": "Name", 'value': this.sourceConfig.group, 'short': true},
            {"title": "Group", 'value': this.targetConfig.group, 'short': true},
            {"title": "Comment", 'value': this.sourceConfig.info, 'short': true},
          ],
        }],
      }
      deployData["attachments"].push({'color': '#9907e8', fields: [{"title": "Differences:"}]})
      let diffs = []
      for (const group of this.groups) {
        let code = group.code;
        let name = group.name;
        let items = group.items;
        if (code === 1 && items.length !== 0){
          diffs.push({"title": name})
          for (const item of items) {
            diffs.push({"value": item.name, 'short': true})
          }
        }
      }
      deployData["attachments"].push({'color': '#92e807', fields: diffs.length !== 0 ? diffs : [{"value": "none", 'short': true}]})
      SlackLogManager.log(deployData)
    },
    async updateInput(){
      await this.reloadReleaseConfigs();
      this.clear();
      this.updateDiffView();
    },
    clear(){
      this.sourceData = null
      this.targetData = null
      this.groups = []
      this.resolved = []
    },
    show(startConfig) {
      this.selectedTab = 2
      this.sourceConfig = startConfig || null
      this.targetConfig = null
      this.clear()
      this.downloadData = false
      this.dialog = true
    },
    hide() {
      this.dialog = false
    },
    onClose() {
      this.hide()
    },
    updateDiffView(){
      this.downloadData = true
      ConfigStoreUtils.getConfigData("configs_data/" + this.sourceConfig.uid)
        .then((storageConfigData) => {
          this.sourceData = storageConfigData
          ConfigStoreUtils.getConfigData("configs_data/" + this.targetConfig.uid)
            .then((storageConfigData) => {
              this.targetData = storageConfigData
              this.groups = []
              this.resolved = []
              this.groups = this.generateGroupsAndValidate(this.sourceData, this.targetData);
              this.downloadData = false

              if (this.hasAnyDiffs(this.groups)) {
                this.$alert("There are differences between your config and the target config. Please resolve them before deploy.");
              } else {
                this.$notify({
                  title: "Configs are identical",
                  text: "",
                  type: "success",
                });
              }

              this.$notify({title: 'Merge available', text: "", type: "success"});
            }).catch((error) => {
            this.downloadData = false
            this.$notify({title: 'Error get Data B', text: error, type: "error"});
          })
        }).catch((error) => {
        this.downloadData = false
        console.warn("Data A not selected: ", error);
      })
    },
    onResolve(resolved, item, stringC){
      const key = item.group + '/' + item.uid;

      if (resolved && resolved.c === true) {
        resolved.a = false;
        resolved.b = false;
        this.customValues[key] = stringC;
      } else {
        delete this.customValues[key];
      }

      this.resolved[key] = resolved;
      this.$forceUpdate();
    },

    generateGroupsAndValidate(sourceData, targetData){
      let groups = []
      let groupKeys = [];
      for (let groupKey in sourceData){groupKeys.push(groupKey);}
      for (let groupKey in targetData){if (!groupKeys.includes(groupKey)){groupKeys.push(groupKey);}}

      for (let i in groupKeys){
        let key = groupKeys[i];

        if (sourceData[key] !== undefined && targetData[key] === undefined){
          if (typeof sourceData[key] != "object"){
            sourceData[key] = [{"one_value_property":sourceData[key]}]
          }
          targetData[key] = []
        }
        if (targetData[key] !== undefined && sourceData[key] === undefined){
          if (typeof targetData[key] != "object"){
            targetData[key] = [{"one_value_property":targetData[key]}]
          }
          sourceData[key] = []
        }

        if (typeof sourceData[key] != "object"){
          sourceData[key] = [{"one_value_property":sourceData[key]}]
        }

        if (typeof targetData[key] != "object"){
          targetData[key] = [{"one_value_property":targetData[key]}]
        }

        let differences = diff(sourceData[key], targetData[key]);
        if (differences === undefined){
          groups.push({code:0, name:key, items:[]})
        }else{
          let items = [];
          let keysA = []

          for (let itemKey in sourceData[key]) { keysA.push(itemKey) }
          let keysB = []
          for (let itemKey in targetData[key]) { keysB.push(itemKey) }
          let output = keysA.concat(keysB);
          output = output.filter(function (item, pos) {
            return output.indexOf(item) === pos
          });
          for (let i in output) {
            let itemKey = output[i]
            differences = diff(sourceData[key][itemKey], targetData[key][itemKey]);
            if (differences !== undefined) {
              let valueA = sourceData[key][itemKey];

              if (valueA === undefined) {
                let name = targetData[key][itemKey]["name"] || itemKey
                items.push({name: name, uid: itemKey, group: key})
              } else {
                let name = sourceData[key][itemKey]["name"] || itemKey
                items.push({name: name, uid: itemKey, group: key})
              }
            }
          }

          groups.push({code:1, name:key, items:items})
        }
      }
      return groups;
    },

    getGroupColor(group){
      let count = 0
      for (let i in group.items){
        let item = group.items[i]
        let key = group.name + '/' + item.uid
        if (this.resolved[key] !== undefined){
          if(this.resolved[key].a || this.resolved[key].b || this.resolved[key].c){ count += 1 }
        }
      }
      if (count === group.items.length) {return 'green darken-3'}
      return 'red'
    },
    getItemColor(item){
      let key = item.group + '/' + item.uid
      if (this.resolved[key] !== undefined && (this.resolved[key].a || this.resolved[key].b || this.resolved[key].c)){
        if (this.resolved[key].a){
          return 'blue-grey darken-1';
        }
        if (this.resolved[key].b) {
          return 'light-blue darken-1';
        }
        if (this.resolved[key].c) {
          return 'purple darken-1';
        }
      }
      return 'red lighten-2'
    },
    getGroupSelectionCounts(group) {
      let a = 0, b = 0, c = 0;
      for (const item of group.items) {
        const key = `${item.group}/${item.uid}`;
        const r = this.resolved[key] || { a:false, b:false, c:false };
        if (r.a) a += 1;
        if (r.b) b += 1;
        if (r.c) c += 1;
      }
      return { a, b, c };
    },

    groupHasSelection(group) {
      for (const item of group.items) {
        const r = this.resolved[`${item.group}/${item.uid}`];
        if (r && (r.a || r.b || r.c)) return true;
      }
      return false;
    },

    checkedA(item){
      const key = item.group + '/' + item.uid
      if (this.resolved[key] === undefined){return false}
      return this.resolved[key].a
    },
    checkedB(item){
      const key = item.group + '/' + item.uid
      if (this.resolved[key] === undefined){return false}
      return this.resolved[key].b
    },
    isCustomChecked(item) {
      const key = item.group + '/' + item.uid;
      return this.resolved[key]?.c === true;
    },

    isNotResolved(){
      let count = 0
      let total = 0

      for (let g in this.groups){
        let items = this.groups[g].items

        for (let i in items){
          total += 1
          let item = items[i]
          let key = item.group + '/' + item.uid

          if (this.resolved[key] !== undefined){
            if(this.resolved[key].a || this.resolved[key].b || this.resolved[key].c) {
              count += 1
            }
          }
        }
      }

      return total !== count;
    },
    checkboxUpdated(key, tag, newValue){
      newValue = newValue || false;

      this.resolved[key] = this.resolved[key] || { a:false, b:false, c:false };

      if (tag === 'a') {
        this.resolved[key].a = newValue;
        this.resolved[key].b = false;
        this.resolved[key].c = false;
        delete this.customValues[key];
      } else {
        this.resolved[key].b = newValue;
        this.resolved[key].a = false;
        this.resolved[key].c = false;
        delete this.customValues[key];
      }

      this.$forceUpdate();
    },
    showItemDetails(item) {
      let key = item.group + '/' + item.uid
      if (this.resolved[key] === undefined){
        this.resolved[key] = {a:false, b:false}
      }

      this.$emit("show-c-diff-preview-popup", this.resolved[key], this.sourceConfig, this.targetConfig, this.sourceData, this.targetData, item)
    },
    getFinalConfig() {
      const copyDataA = JSON.parse(JSON.stringify(this.sourceData));
      const copyDataB = JSON.parse(JSON.stringify(this.targetData));
      let finalData = JSON.parse(JSON.stringify(copyDataA));

      for (let r in this.resolved) {
        const res = this.resolved[r];
        const [group, uid] = r.split("/");

        if (res.c) {
          try {
            const str = this.customValues[r];
            const parsedC = typeof str === 'string' ? JSON.parse(str) : str;

            if (!finalData[group]) finalData[group] = {};
            finalData[group][uid] = parsedC;
          } catch (e) {
            console.warn(`Invalid JSON in customValues for ${r}`, e);
          }
          continue;
        }

        if (res.b) {
          const value = copyDataB[group]?.[uid];

          if (value !== undefined) {
            if (!finalData[group]) finalData[group] = {};
            finalData[group][uid] = value;
          } else {
            if (Object.hasOwn(finalData[group]?.[uid], 'one_value_property')) {
              finalData[group][uid]["one_value_property"] = '';
              continue;
            }
            delete finalData[group]?.[uid];
          }
        }
      }

      const model = new ConfigModel(
        this.sourceConfig.group + '_merged_with_' + this.targetConfig.group,
        this.sourceConfig.mapId
      );
      model.historyId = this.getUUID();

      for (let item in finalData) {
        if (Array.isArray(finalData[item]) &&
          finalData[item].length === 1 &&
          Object.hasOwn(finalData[item][0], 'one_value_property')) {
          finalData[item] = finalData[item][0]["one_value_property"];
        }
      }

      return { config: model, content: finalData, a: copyDataA, b: copyDataB };
    },
    getUUID(){
      return uuid.v4().replace(/-/g, "")
    },
    async reloadReleaseConfigs() {
      try {
        this.downloadData = true;
        await ConfigStoreUtils.fetchReleaseConfigs();
      } catch (e) {
        console.error("reloadReleaseConfigs failed:", e);
        this.$notify({
          title: "Failed to reload release configs",
          text: String(e),
          type: "error",
        });
      } finally {
        this.downloadData = false;
      }
    },
    async validateNewerVersion() {
      if (!this.sourceConfig || !this.targetConfig) return false;

      this.downloadData = true;
      try {
        const targetConfigRefreshed = await ConfigStoreUtils.getConfigData("configs_data/" + this.targetConfig.uid);
        const groupsRefreshed = this.generateGroupsAndValidate(this.targetData, targetConfigRefreshed);
        const hasDiffs = this.hasAnyDiffs(groupsRefreshed);
        if (hasDiffs) {
          this.targetData = targetConfigRefreshed;
          this.groups = [];
          this.resolved = [];
          this.groups = this.generateGroupsAndValidate(this.sourceData, this.targetData);
          return true;
        }
        return false;
      } finally {
        this.downloadData = false;
      }
    },
    hasAnyDiffs(groups) {
      return groups.some(g => g.code === 1 && g.items && g.items.length > 0);
    },
  },
}
</script>

<style scoped>
.merge-card {
  height: 100vh;
}

.min-h-0 {
  min-height: 0;
}

.overflow-y-auto {
  overflow-y: auto;
}
</style>
